import Image from 'next/image';

import styled from '../../theme/styled-with-theme';
import { mediaQueries } from '../../styles/media-queries';
import { pxToRem } from '../../styles/utils';

import { Flexbox } from '../Flexbox';
import { Text } from '../Text';

import { NodeProps } from '../RichText/Nodes/shared';

import greenCheck from '@/images/unordered-list-green-check.svg';
import greyCross from '@/images/greyed-cross.svg';

interface PlanInfoProps {
  color?: string;
  expand?: boolean;
  lightText?: boolean;
  topGradient?: string;
  bottomGradient?: string;
  visible?: boolean;
}

export const Container = styled(Flexbox)`
  position: relative;
  height: fit-content;
  width: 100%;
`;

export const PlanInfoWrapper = styled(Flexbox)<PlanInfoProps>`
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;

  background: ${({ theme }) => theme.color.black};
  border: ${({ theme }) => `${pxToRem(2)} solid ${theme.color.black}`};
  border-radius: ${({ theme }) => theme.borderRadius.planHeaderMobile};
  box-shadow: ${({ theme }) => `-4px 4px 0px 0px ${theme.color.black}`};

  width: 100%;
  height: ${({ expand = false }) => (expand ? '100%' : 'fit-content')};
`;

export const PlanInfoHeader = styled(Flexbox)<PlanInfoProps>`
  background-color: ${({ color }) => `#${color}`};
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: fit-content;
  max-height: ${pxToRem(126)};
  border-radius: ${({ theme }) =>
    `${theme.borderRadius.planHeaderMobile} ${theme.borderRadius.planHeaderMobile} 0 0`};

  ${mediaQueries[0]} {
    padding: ${({ theme }) => theme.spacing.ml};
  }

  ${mediaQueries.min} {
    padding: ${({ theme }) => theme.spacing.l};
  }

  @media (min-width: 321px) and (max-width: 375px) {
    padding: ${({ theme }) => theme.spacing.ml};
  }
`;

export const Badge = styled(Flexbox)`
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;

  ${mediaQueries[0]} {
    width: ${pxToRem(65)};
    height: ${pxToRem(60)};
    right: ${pxToRem(12)};
  }

  ${mediaQueries.min} {
    width: ${pxToRem(68)};
    height: ${pxToRem(63)};
    right: ${pxToRem(24)};
  }

  @media (min-width: 321px) and (max-width: 375px) {
    width: ${pxToRem(65)};
    height: ${pxToRem(60)};
    right: ${pxToRem(12)};
  }

  ${mediaQueries.tablet} {
    right: ${pxToRem(32)};
  }

  ${mediaQueries.desktop} {
    width: ${pxToRem(90)};
    height: ${pxToRem(90)};
    right: ${pxToRem(4)};
    top: -${pxToRem(35)};
  }
`;

export const BadgeIcon = styled(Image)`
  width: 100%;
  height: 100%;
`;

export const BadgeText = styled(Text)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  line-height: ${pxToRem(13.44)};
`;

export const PlanTitle = styled(Flexbox)<PlanInfoProps>`
  color: ${({ theme, lightText }) =>
    lightText ? theme.color.white : theme.color.footerBgSecondary};
  flex-direction: column;
  border-bottom: 0;
`;

export const PlanText = styled(Text)`
  ${({ theme }) => `
    font-weight: ${theme.fontWeight.h1};
    margin: 0;

    ${mediaQueries[0]}{
      font-size: ${theme.fontSize.l};
    }

    ${mediaQueries.min}{
      font-size: ${theme.fontSize.xl};
    }
    
    @media (min-width: 321px) and (max-width: 375px) {
      font-size: ${theme.fontSize.l};
    }
    
    ${mediaQueries.mobile} {
      font-size: ${theme.fontSize['2xl']};
    }
    
    ${mediaQueries.tablet} {  
      font-size: ${theme.fontSize.xl};
    }
  `}
`;

export const PriceText = styled(Text)`
  ${({ theme }) => `
    ${mediaQueries[0]}{
      font-size: ${theme.fontSize.sm};
    }

    ${mediaQueries.min}{
      font-size: ${theme.fontSize.m};
    }

    @media (min-width: 321px) and (max-width: 375px) {
      font-size: ${theme.fontSize.sm};
    }
  `}
`;

export const PlanDetails = styled(Flexbox)<PlanInfoProps>`
  background-color: ${({ theme }) => theme.color.white};
  width: 100%;
  min-height: ${pxToRem(128)};
  border-style: solid none solid none;
  border-width: ${pxToRem(2.5)};
  display: flex;
  flex-direction: column;
  flex: 1;

  ${mediaQueries[0]} {
    padding: ${({ theme }) => theme.spacing.ml};
  }

  ${mediaQueries.min} {
    padding: ${({ theme }) => theme.spacing.l};
  }

  @media (min-width: 321px) and (max-width: 375px) {
    padding: ${({ theme }) => theme.spacing.ml};
  }
`;

export const DescriptionWrapper = styled(Flexbox)`
  display: flex;
  width: 100%;

  ${mediaQueries.desktop} {
    padding: 0;
    p {
      font-size: ${({ theme }) => theme.fontSize.m};
    }
  }
`;

export const Details = styled(Flexbox)`
  p {
    &:not(:last-child) {
      margin-bottom: 0;
    }
  }
`;

export const PlanList = styled('ul', {
  shouldForwardProp: (prop) => prop !== 'useCheckMark'
})<{ useCheckMark?: boolean }>`
  margin: 0;
  list-style-image: ${({ useCheckMark = true }) =>
    useCheckMark ? `url(${greenCheck.src})` : `url(${greyCross.src})`};
`;

export const PlanListItem = styled('li')<NodeProps>`
  padding-left: ${({ theme }) => theme.spacing.s};
  margin-left: -${pxToRem(12)};
  margin-bottom: ${({ theme }) => theme.spacing.m};
`;

export const SeeMoreWrapper = styled(Flexbox)`
  width: 100%;
  height: fit-content;
  max-height: ${pxToRem(90)};

  background-color: ${({ theme }) => theme.color.white};
  border-radius: ${({ theme }) =>
    `0 0 ${theme.borderRadius.planHeaderMobile} ${theme.borderRadius.planHeaderMobile}`};
  cursor: pointer;
  transition: 0.25s border-radius ease;

  ${mediaQueries[0]} {
    padding: ${({ theme }) => theme.spacing.ml};
  }

  ${mediaQueries.min} {
    padding: ${({ theme }) => theme.spacing.l};
  }

  @media (min-width: 321px) and (max-width: 375px) {
    padding: ${({ theme }) => theme.spacing.ml};
  }
`;

export const ToggleIcon = styled(Image, {
  shouldForwardProp: (prop) => prop !== 'isOpen'
})<{ isOpen: boolean }>`
  height: ${pxToRem(20)};
  width: ${pxToRem(20)};
  transition: 0.25s transform ease;
  transform: ${({ isOpen = false }) => (isOpen ? `rotate(45deg)` : 'none')};
`;
